import React from 'react';
import { Dispatch } from 'redux';
import { compose, withHandlers } from 'recompose';
import { connect } from 'react-redux';
import { AvailablePage } from '@mm/state/interfaces/connected-page';
import { StoreState } from '@mm/state/store-interfaces';
import './FacebookPageConnector.pcss';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import onboardingActions from '@mm/state/modules/onboardingState/actions';
import {
  OnboardingSignupStep,
  UserAttributes,
} from '@mm/state/model/CompanyUsersModel';
import usersCRUDActions from '@mm/state/actions/UsersCRUDActions';
import companyCRUDActions from '@mm/state/actions/CompanyCRUDActions';
import { openGetStartedOnboardingModal } from '@mm/state/modules/modalsState/actions';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { useSetNextOnboardingStep } from '../../../use-set-next-onboarding-step';
import { botFromScratch } from '../../../../../../common/modals/GetStartedOnboardingModal/GetStartedOnboardingModal';
import sessionCRUDActions from '@mm/state/actions/SessionCRUDActions';
import { SessionObject } from '@mm/state/interfaces/session';
import { AfterPageConnect } from '@mm/state/modules/modalsState/props';
import connectedPageCRUDActions from '@mm/state/actions/ConnectedPageCRUDActions';

type OuterProps = {
  showJoinFacebookPage?: boolean;
  pageInfo: AvailablePage;
  afterPageConnect: AfterPageConnect;
  onboarding: boolean;
  disabled?: boolean;
  onConnectBtnClick?: () => void;
};

type ConnectPageHandler = {
  onButtonClick: (pageInfo: AvailablePage) => Promise<any>;
};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  ConnectPageHandler;

type Props = OuterProps & InnerProps;

const FacebookPageConnector = ({
  pageInfo,
  onButtonClick,
  showJoinFacebookPage,
  onConnectBtnClick,
  onboarding,
  disabled,
}: Props) => {
  const { setNextOnboardingStep } = useSetNextOnboardingStep();
  const showPageConnectedMessage =
    !showJoinFacebookPage && pageInfo.companyId && onboarding;

  const onConnectJoinBtnClick = () => {
    if (onConnectBtnClick) {
      onConnectBtnClick();
    }
    onButtonClick(pageInfo);
  };

  return (
    <div className="connected-page-item">
      <div className="connected-page-item__heading-container">
        {pageInfo.name}
      </div>

      {showPageConnectedMessage && (
        <PrimaryButton
          disabled={disabled}
          className="connected-page-item__connect-button"
          onClick={() => setNextOnboardingStep(OnboardingSignupStep.USER_INFO)}
        >
          Next
        </PrimaryButton>
      )}

      {!showPageConnectedMessage && (
        <PrimaryButton
          disabled={disabled}
          className="connected-page-item__connect-button"
          onClick={onConnectJoinBtnClick}
        >
          {pageInfo.companyId ? 'Join' : 'Connect'}
        </PrimaryButton>
      )}
    </div>
  );
};

const onButtonClick = (props: Props) => async (pageInfo: AvailablePage) => {
  const {
    companyId,
    afterPageConnect,
    getPages,
    connectPage,
    session,
    updateUserAttributes,
    activateCompany,
    getCompanies,
    onboarding,
    updateSession,
    retrieveFacebookPage,
    // openGetStartedOnboardingModalLocal,
  } = props;

  // Note 1: Separating the case when the user is joining a FB page (pageInfo.companyId)
  // and when he connects a new FB page, since in the later case we need to show modal to select bot template.
  // Note 2: If no facebookPage means promise failed and was intercepted by commonHandler for permissions
  // or other reasons
  if (pageInfo.companyId) {
    const response = await connectPage(companyId, pageInfo, onboarding);

    if (response && response.facebookPage) {
      await updateSession({ needsToConnectPage: false });
      await getCompanies();
      await activateCompany(pageInfo.companyId, session.id);
      await updateUserAttributes(pageInfo.companyId, session.id, {
        onboardingSignupStep: OnboardingSignupStep.NONE,
      });
    }
  } else {
    const res = await connectPage(
      companyId,
      pageInfo,
      onboarding,
      botFromScratch.id,
    );
    await getPages();
    if (res?.facebookPage) {
      await retrieveFacebookPage(res.facebookPage.id);
      updateSession({ needsToConnectPage: false });
      await afterPageConnect(res);
    }

    // temporary disabled for UX experiment
    // openGetStartedOnboardingModalLocal(afterPageConnect, onboarding, pageInfo);
  }
};

const mapStateToProps = (state: StoreState) => ({
  session: state.session.session,
  companyId: selectActiveCompanyId(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  connectPage: (
    companyId: number,
    pageInfo: AvailablePage,
    onboarding: boolean,
    templateBotId?: number,
  ) =>
    dispatch(
      onboardingActions.connectPage(
        companyId,
        pageInfo,
        onboarding,
        templateBotId,
      ),
    ),
  updateUserAttributes: (
    companyId: number,
    entityId: number,
    userAttributes: Partial<UserAttributes>,
  ) =>
    dispatch(
      usersCRUDActions.updateUserAttributes(
        companyId,
        entityId,
        userAttributes,
      ),
    ),
  activateCompany: (id: number, entityId: number) =>
    dispatch(usersCRUDActions.updateActiveCompanyId(entityId, id)),
  getCompanies: () => dispatch(companyCRUDActions.retrieveAll()),
  getPages: () => dispatch(connectedPageCRUDActions.retrieveAll()),
  retrieveFacebookPage: (facebookPageId: number) =>
    dispatch(connectedPageCRUDActions.retrievePage(facebookPageId)),
  openGetStartedOnboardingModalLocal: (
    afterPageConnect: AfterPageConnect,
    onboarding: boolean,
    pageInfo: AvailablePage,
  ) =>
    dispatch(
      openGetStartedOnboardingModal(afterPageConnect, onboarding, pageInfo),
    ),
  updateSession: (s: Partial<SessionObject>) =>
    dispatch(sessionCRUDActions.update(s)),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
  withHandlers({ onButtonClick }),
);

export default enhance(FacebookPageConnector);
